import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../../../applicationUi/components/Table";
import { format } from "date-fns";
import { fetchClinicianAvailabilityReport } from "../../../../store/thunks/reportsThunk";
import * as XLSX from "xlsx";
import { toast } from 'react-toastify';

const ClinicianAvailabilityReport = () => {
  const dispatch = useDispatch();
  const [startDate, setStartDate] = useState(format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [activeView, setActiveView] = useState("summary");
  const [selectedClinician, setSelectedClinician] = useState(null);

  const {
    data: availabilityData,
    loading,
    error,
  } = useSelector((state) => state.reports.clinicianAvailability || { data: null, loading: false, error: null });

  useEffect(() => {
    dispatch(fetchClinicianAvailabilityReport({ startDate, endDate }));
  }, [dispatch, startDate, endDate]);

  const data = availabilityData?.data || {};
  const summary = data.summary || {};
  const clinicians = data.clinicians || [];

  const handleDateChange = (field, value) => {
    if (field === "startDate") {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
  };

  const handleDownload = (viewType) => {
    let data = [];
    let fileName = "";
    let sheetName = "";

    switch (viewType) {
      case "summary":
        data = clinicians.map((clinician) => ({
          "Clinician": clinician.clinicianName,
          "Job Title": clinician.jobTitle || "N/A",
          "Employment Status": clinician.employmentStatus || "N/A",
          "Specialization": Array.isArray(clinician.specialization) ? clinician.specialization.join(", ") : "N/A",
          "Default Location": clinician.defaultLocation || "N/A",
          "Scheduled Hours": clinician.totalScheduledHours,
          "Booked Hours": clinician.totalBookedHours,
          "Available Hours": clinician.availableHours,
          "Utilization Rate": `${clinician.utilizationRate}%`,
          "Total Appointments": clinician.appointmentCount,
        }));
        fileName = `Clinician_Availability_Summary_${startDate}_to_${endDate}.xlsx`;
        sheetName = "Availability Summary";
        break;
      case "daily":
        if (!selectedClinician) {
          toast.warning("Please select a clinician first.");
          return;
        }
        data = Object.entries(selectedClinician.dailyBreakdown).map(([date, dayData]) => ({
          "Date": date,
          "Appointment Count": dayData.appointmentCount,
          "Booked Hours": dayData.bookedHours,
          "Appointments": dayData.appointments.map(apt => `${apt.title} (${format(new Date(apt.start), "HH:mm")}-${format(new Date(apt.end), "HH:mm")})`).join("; "),
        }));
        fileName = `${selectedClinician.clinicianName.replace(/\s+/g, '_')}_Daily_Availability_${startDate}_to_${endDate}.xlsx`;
        sheetName = "Daily Breakdown";
        break;
      default:
        return;
    }

    if (data.length === 0) {
      toast.warning("No data available to download.");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, fileName);
  };

  const summaryColumns = [
    { header: "Clinician", accessor: "clinicianName" },
    { header: "Job Title", accessor: "jobTitle" },
    { header: "Employment", accessor: "employmentStatus" },
    { header: "Scheduled Hours", accessor: "totalScheduledHours" },
    { header: "Booked Hours", accessor: "totalBookedHours" },
    { header: "Available Hours", accessor: "availableHours" },
    { 
      header: "Utilization Rate", 
      accessor: (row) => `${row.utilizationRate}%`,
      cell: (row) => {
        const rate = parseFloat(row.utilizationRate);
        let colorClass = "text-green-600";
        if (rate < 60) colorClass = "text-red-600";
        else if (rate < 80) colorClass = "text-yellow-600";
        
        return (
          <span className={`font-semibold ${colorClass}`}>
            {row.utilizationRate}%
          </span>
        );
      }
    },
    { header: "Appointments", accessor: "appointmentCount" },
  ];

  const dailyColumns = selectedClinician ? [
    { header: "Date", accessor: (row) => row[0] },
    { header: "Appointments", accessor: (row) => row[1].appointmentCount },
    { header: "Booked Hours", accessor: (row) => row[1].bookedHours },
    { 
      header: "Details", 
      accessor: (row) => {
        const appointments = row[1].appointments;
        if (appointments.length === 0) return "No appointments";
        return appointments.map(apt => 
          `${apt.title} (${format(new Date(apt.start), "HH:mm")}-${format(new Date(apt.end), "HH:mm")})`
        ).join(", ");
      }
    },
  ] : [];

  if (loading) return <div className="flex justify-center items-center h-64">Loading...</div>;
  if (error) return <div className="text-red-500 p-4">{error}</div>;

  const dailyData = selectedClinician ? Object.entries(selectedClinician.dailyBreakdown) : [];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white p-6 rounded-sm shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Date Range & Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => handleDateChange("startDate", e.target.value)}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => handleDateChange("endDate", e.target.value)}
              className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>
          {activeView === "daily" && (
            <div>
              <label htmlFor="clinicianSelect" className="block text-sm font-medium text-gray-700 mb-1">
                Select Clinician
              </label>
              <select
                id="clinicianSelect"
                value={selectedClinician?.clinicianId || ""}
                onChange={(e) => {
                  const clinician = clinicians.find(c => c.clinicianId === e.target.value);
                  setSelectedClinician(clinician);
                }}
                className="block w-full rounded-sm border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="">Choose a clinician...</option>
                {clinicians.map((clinician) => (
                  <option key={clinician.clinicianId} value={clinician.clinicianId}>
                    {clinician.clinicianName}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white p-6 rounded-sm shadow-sm">
          <h4 className="text-sm font-medium text-gray-500">Total Clinicians</h4>
          <p className="text-2xl font-bold text-gray-900">{summary.totalClinicians || 0}</p>
        </div>
        <div className="bg-white p-6 rounded-sm shadow-sm">
          <h4 className="text-sm font-medium text-gray-500">Total Scheduled Hours</h4>
          <p className="text-2xl font-bold text-blue-600">{summary.totalScheduledHours || "0.00"}</p>
        </div>
        <div className="bg-white p-6 rounded-sm shadow-sm">
          <h4 className="text-sm font-medium text-gray-500">Total Booked Hours</h4>
          <p className="text-2xl font-bold text-green-600">{summary.totalBookedHours || "0.00"}</p>
        </div>
        <div className="bg-white p-6 rounded-sm shadow-sm">
          <h4 className="text-sm font-medium text-gray-500">Total Available Hours</h4>
          <p className="text-2xl font-bold text-red-600">{summary.totalAvailableHours || "0.00"}</p>
        </div>
        <div className="bg-white p-6 rounded-sm shadow-sm">
          <h4 className="text-sm font-medium text-gray-500">Average Utilization</h4>
          <p className="text-2xl font-bold text-purple-600">{summary.averageUtilization || "0.00"}%</p>
        </div>
      </div>

      {/* View Tabs */}
      <div className="bg-white shadow-sm rounded-sm">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {[
              { key: "summary", label: "Summary" },
              { key: "details", label: "Detailed View" },
              { key: "daily", label: "Daily Breakdown" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveView(tab.key)}
                className={`${
                  activeView === tab.key
                    ? "border-indigo-500 text-indigo-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeView === "summary" && (
            <Table
              columns={summaryColumns}
              data={clinicians}
              title="Clinician Availability Summary"
              description="Overview of clinician availability and utilization rates"
              actions={[
                {
                  label: "Download Excel",
                  onClick: () => handleDownload("summary"),
                },
              ]}
            />
          )}

          {activeView === "details" && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Detailed Clinician Information</h3>
              
              <div className="grid grid-cols-1 gap-6">
                {clinicians.map((clinician) => (
                  <div key={clinician.clinicianId} className="bg-gray-50 p-6 rounded-sm border">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* Basic Info */}
                      <div className="space-y-2">
                        <h4 className="font-semibold text-lg text-gray-900">{clinician.clinicianName}</h4>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Job Title:</span> {clinician.jobTitle || "N/A"}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Email:</span> {clinician.email}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Employment:</span> {clinician.employmentStatus || "N/A"}
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Specialization:</span> {
                            Array.isArray(clinician.specialization) 
                              ? clinician.specialization.join(", ") 
                              : "N/A"
                          }
                        </p>
                      </div>

                      {/* Availability Metrics */}
                      <div className="space-y-2">
                        <h5 className="font-medium text-gray-900">Availability Metrics</h5>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Scheduled:</span> {clinician.totalScheduledHours} hours
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Booked:</span> {clinician.totalBookedHours} hours
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Available:</span> {clinician.availableHours} hours
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Utilization:</span> 
                          <span className={`ml-1 font-semibold ${
                            parseFloat(clinician.utilizationRate) >= 80 ? "text-green-600" :
                            parseFloat(clinician.utilizationRate) >= 60 ? "text-yellow-600" : "text-red-600"
                          }`}>
                            {clinician.utilizationRate}%
                          </span>
                        </p>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Appointments:</span> {clinician.appointmentCount}
                        </p>
                      </div>

                      {/* Clinic Associations */}
                      <div className="space-y-2">
                        <h5 className="font-medium text-gray-900">Clinic Information</h5>
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Default Location:</span> {clinician.defaultLocation || "N/A"}
                        </p>
                        {clinician.associatedClinics && clinician.associatedClinics.length > 0 && (
                          <div>
                            <span className="font-medium text-sm text-gray-600">Associated Clinics:</span>
                            <ul className="text-sm text-gray-600 ml-2">
                              {clinician.associatedClinics.map((clinic, index) => (
                                <li key={index}>
                                  • {clinic.clinicName} {clinic.isDefault && "(Default)"}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Appointment Breakdown */}
                    {(Object.keys(clinician.appointmentsByStatus).length > 0 || Object.keys(clinician.appointmentsByType).length > 0) && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {Object.keys(clinician.appointmentsByStatus).length > 0 && (
                            <div>
                              <h6 className="font-medium text-gray-900 mb-2">By Status</h6>
                              <div className="space-y-1">
                                {Object.entries(clinician.appointmentsByStatus).map(([status, count]) => (
                                  <p key={status} className="text-sm text-gray-600">
                                    {status}: {count}
                                  </p>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {Object.keys(clinician.appointmentsByType).length > 0 && (
                            <div>
                              <h6 className="font-medium text-gray-900 mb-2">By Type</h6>
                              <div className="space-y-1">
                                {Object.entries(clinician.appointmentsByType).slice(0, 5).map(([type, count]) => (
                                  <p key={type} className="text-sm text-gray-600">
                                    {type}: {count}
                                  </p>
                                ))}
                                {Object.keys(clinician.appointmentsByType).length > 5 && (
                                  <p className="text-sm text-gray-500 italic">
                                    ... and {Object.keys(clinician.appointmentsByType).length - 5} more
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeView === "daily" && (
            <div>
              {!selectedClinician ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">Please select a clinician from the dropdown above to view their daily breakdown.</p>
                </div>
              ) : (
                <Table
                  columns={dailyColumns}
                  data={dailyData}
                  title={`Daily Breakdown - ${selectedClinician.clinicianName}`}
                  description={`Day-by-day availability analysis for ${selectedClinician.clinicianName}`}
                  actions={[
                    {
                      label: "Download Excel",
                      onClick: () => handleDownload("daily"),
                    },
                  ]}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClinicianAvailabilityReport;
